import type React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  className?: string;
  description?: string;
}

// Animation variants for the card content
const contentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
    },
  },
};

// Animation for the value counter
const valueVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 15,
      delay: 0.2,
    },
  },
};

export const MetricCard = ({ title, value, icon, className, description }: MetricCardProps) => {
  return (
    <Card className={cn("overflow-hidden h-full", className)}>
      <CardContent className="p-4">
        <motion.div className="flex flex-col" variants={contentVariants} initial="hidden" animate="visible">
          <motion.div className="flex items-center justify-between" variants={itemVariants}>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            {icon && (
              <motion.div
                className="text-muted-foreground"
                whileHover={{
                  rotate: [0, -5, 5, -5, 0],
                  transition: { duration: 0.5 },
                }}
              >
                {icon}
              </motion.div>
            )}
          </motion.div>
          <motion.div className="mt-2 flex items-baseline" variants={valueVariants}>
            <motion.h3
              className="text-2xl font-semibold tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  delay: 0.3,
                },
              }}
            >
              {value}
            </motion.h3>
          </motion.div>
          {description && (
            <motion.p className="mt-1 text-xs text-muted-foreground" variants={itemVariants}>
              {description}
            </motion.p>
          )}
        </motion.div>
      </CardContent>
    </Card>
  );
};
